export type DependencyGraphNode = {
  name: string;
  dependencies?: Array<string> | null;
};

export type DeployOrderResult =
  | { hasCycle: false; order: Array<string> }
  | { hasCycle: true; cyclicNames: Array<string> };

type TopoSortResult<K> =
  | { hasCycle: false; sorted: Array<K> }
  | { hasCycle: true; cyclic: Array<K> };

/** Kahn's algorithm; mirrors the backend's generic TopologicalSort (toposort.go). */
function topologicalSort<K>(
  nodes: Array<K>,
  dependencies: Map<K, Array<K>>
): TopoSortResult<K> {
  const inDegree = new Map<K, number>(nodes.map((node) => [node, 0]));
  const dependents = new Map<K, Array<K>>();

  nodes.forEach((node) => {
    (dependencies.get(node) ?? []).forEach((dependency) => {
      inDegree.set(node, (inDegree.get(node) ?? 0) + 1);
      dependents.set(dependency, [...(dependents.get(dependency) ?? []), node]);
    });
  });

  const queue = nodes.filter((node) => inDegree.get(node) === 0);
  const sorted: Array<K> = [];

  for (let i = 0; i < queue.length; i += 1) {
    const node = queue[i];
    sorted.push(node);

    (dependents.get(node) ?? []).forEach((dependent) => {
      const remaining = (inDegree.get(dependent) ?? 0) - 1;
      inDegree.set(dependent, remaining);
      if (remaining === 0) {
        queue.push(dependent);
      }
    });
  }

  if (sorted.length < nodes.length) {
    return {
      hasCycle: true,
      cyclic: nodes.filter((node) => (inDegree.get(node) ?? 0) > 0),
    };
  }

  return { hasCycle: false, sorted };
}

/**
 * Topologically sorts artifacts by their dependencies, mirroring the
 * server-side sort used at deploy time so the wizard can preview the resulting order.
 */
export function deriveDeployOrder(
  artifacts: Array<DependencyGraphNode>
): DeployOrderResult {
  const names = artifacts.map((artifact) => artifact.name);
  const dependencies = new Map(
    artifacts.map(
      (artifact) =>
        [
          artifact.name,
          (artifact.dependencies ?? []).filter(
            (dependency): dependency is string => !!dependency
          ),
        ] as const
    )
  );

  const result = topologicalSort(names, dependencies);

  return result.hasCycle
    ? { hasCycle: true, cyclicNames: result.cyclic }
    : { hasCycle: false, order: result.sorted };
}
